import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { ApiError } from "../api/client";
import { useAuthConfig, useLogin } from "../api/hooks";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { useT } from "../i18n";

/** Keys for `/login?error=…`, set by the OIDC views (spec §19.1). */
const OIDC_ERRORS: Record<string, string> = {
  oidc_unavailable: "auth.error.unavailable",
  oidc_group: "auth.error.group",
  oidc_failed: "auth.error.failed",
};

const safeNext = (next: string | undefined) => (next && next.startsWith("/") && !next.startsWith("//") ? next : undefined);

export function LoginPage() {
  const t = useT();
  const login = useLogin();
  const config = useAuthConfig();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { next?: string; error?: string };
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // with password login off, superusers still may (spec §19.1, Q65): the form waits behind a link
  const [adminForm, setAdminForm] = useState(false);
  const next = safeNext(search.next);
  // offline or before the config arrives: the password form, as before M13
  const passwordLogin = config.data?.passwordLogin ?? true;
  const oidc = config.data?.oidc.enabled ? config.data.oidc : null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    await login.mutateAsync({ username, password });
    void navigate({ to: next ?? "/" });
  };
  const oidcHref = `/api/auth/oidc/login${next ? `?next=${encodeURIComponent(next)}` : ""}`;
  const oidcError = search.error ? t(OIDC_ERRORS[search.error] ?? OIDC_ERRORS.oidc_failed!) : null;
  const disabled = login.error instanceof ApiError && login.error.status === 403;
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="kss-surface w-80 space-y-3 p-6">
        <h1 className="text-[17px] font-semibold">{t("auth.signIn")}</h1>
        {oidcError && (
          <p className="text-sm text-err" data-testid="login-error">
            {oidcError}
          </p>
        )}
        {oidc && (
          // a full navigation: Django redirects to Keycloak and back (Authorization Code flow)
          <a href={oidcHref} className="flex h-9 w-full items-center justify-center rounded-sm bg-accent px-3.5 text-[13.5px] font-medium text-white transition hover:bg-accent-ink" data-testid="oidc-login">
            {t("auth.withProvider", { provider: oidc.label })}
          </a>
        )}
        {oidc && passwordLogin && <div className="text-center text-xs text-neutral-500">{t("auth.orLocal")}</div>}
        {!passwordLogin && !adminForm && (
          <button type="button" className="block w-full text-center text-xs text-neutral-500 underline hover:text-neutral-900 dark:hover:text-neutral-100" onClick={() => setAdminForm(true)} data-testid="admin-login">
            {t("auth.adminSignIn")}
          </button>
        )}
        {!passwordLogin && adminForm && <div className="text-center text-xs text-neutral-500">{t("auth.adminOnly")}</div>}
        {(passwordLogin || adminForm) && (
          <form onSubmit={submit} className="space-y-3">
            <Input name="username" placeholder={t("auth.username")} autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} />
            <Input name="password" type="password" placeholder={t("auth.password")} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            {login.isError && <p className="text-sm text-err">{disabled ? t("auth.passwordDisabled") : t("auth.invalid")}</p>}
            <Button type="submit" variant={oidc ? "ghost" : undefined} disabled={login.isPending} className={`w-full justify-center ${oidc ? "border border-neutral-300 dark:border-neutral-700" : ""}`}>
              {t("auth.submit")}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
